import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Box, 
  Typography, 
  CircularProgress, 
  Pagination, 
  TablePagination, 
  Chip,
  Divider,
  Stack,
  Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import StorageIcon from '@mui/icons-material/Storage';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TimerIcon from '@mui/icons-material/Timer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Tooltip from '@mui/material/Tooltip';

/**
 * Modal for previewing segment data
 */
const SegmentDataPreviewModal = ({ 
  open, 
  handleClose, 
  data, 
  loading, 
  error,
  onPageChange,
  onRowsPerPageChange
}) => {
  const theme = useTheme();
  
  // If no data or loading, show loading indicator
  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <StorageIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Segment Data Preview</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: 400 
            }}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading segment data...
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
  
  // If error, show error message
  if (error) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <StorageIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Segment Data Preview</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Unable to load segment data. Please try again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => onPageChange(1)}
          >
            Retry
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
  
  // If no data available or no actual data within
  if (!data) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <StorageIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Segment Data Preview</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            No data available for preview.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
  
  // Handle nested data structure from API
  // The API response has a nested structure: { data: { data: {}, ... } }
  // We need to unwrap it properly
  const actualData = data.data ? data.data : data;
  
  // Check if we have the inner data structure. If the API returned
  // something we don't recognise, surface a polite message and let the
  // user retry. The previous variant printed the entire JSON response
  // verbatim - hostile for non-technical users and a leakage risk if
  // the body contained anything sensitive.
  if (!actualData.data || !actualData.pagination) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <StorageIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Segment Data Preview</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Couldn't read the preview response
            </Typography>
            <Typography variant="body2">
              The server returned an unexpected response shape. Try again -
              if the problem persists, a maintainer can inspect the
              request in the API server logs.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onPageChange(1)}
          >
            Retry
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
  
  // Extract pagination info. Defensive clamp on ``current_page`` so
  // a stale page index from the parent (e.g. user was on page 34
  // when they changed features and the new result only has 1 page)
  // never renders as "Page 34 of 1" - even for the single render
  // before the parent's prop-sync effect catches up.
  const _pag = actualData.pagination || {};
  const total_pages = Math.max(1, Number(_pag.total_pages) || 1);
  const total_rows = Math.max(0, Number(_pag.total_rows) || 0);
  const page_size = Math.max(1, Number(_pag.page_size) || 100);
  const current_page = Math.max(
    1, Math.min(total_pages, Number(_pag.current_page) || 1),
  );
  
  // Extract column names from the data
  const dataObject = actualData.data || {};
  const columns = Object.keys(dataObject);
  // Remove timestamp from columns as we'll handle it separately
  const dataColumns = columns.filter(col => col !== 'timestamp');
  // Timestamps array
  const timestamps = dataObject.timestamp || [];
  
  // Extract segment info
  const { 
    name = 'Segment Preview', 
    interval_seconds = 0,
    tolerance_seconds = 0
  } = actualData.segment_info || {};
  
  // Extract field info. ``field_meta`` carries marketplace metadata
  // (listing title, seller label, original key) for any
  // ``mkt:<id>:<key>`` aliases - emitted by the BE so column headers
  // can render "value (Listing Title)" instead of the raw alias.
  const { features = [], labels = [], field_meta: fieldMeta = {} } = actualData.field_info || {};
  
  // Prepare time range display
  const timeRange = actualData.time_range || {};
  const startTime = timeRange.start ? new Date(timeRange.start) : null;
  const endTime = timeRange.end ? new Date(timeRange.end) : null;

  const formatDateUTC = (date, withTime = false) => {
    if (!date || isNaN(date.getTime())) return 'N/A';
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      ...(withTime
        ? {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          }
        : {})
    });
    return formatter.format(date);
  };
  
  // Format timestamps for display
  const formatTimestamp = (timestamp) => {
    try {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return timestamp; // If invalid date, return the original string
      return `${formatDateUTC(date, true)} UTC`;
    } catch (e) {
      console.error('Error formatting timestamp:', e, timestamp);
      return timestamp || 'N/A';
    }
  };

  const formatNumericValue = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return String(value);
    return Number.isInteger(value) ? String(value) : Number(value).toFixed(4);
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <StorageIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{name} - Data Preview</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {total_rows} total rows
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {/* Summary Information */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {/* Time Range */}
            <Chip 
              icon={<CalendarTodayIcon />} 
              label={`Range: ${formatDateUTC(startTime)} - ${formatDateUTC(endTime)}`} 
              variant="outlined" 
            />
            
            {/* Interval */}
            <Chip 
              icon={<TimerIcon />} 
              label={`Interval: ${interval_seconds}s`} 
              variant="outlined" 
            />
            
            {/* Tolerance */}
            <Chip 
              icon={<AccessTimeIcon />} 
              label={`Tolerance: ${tolerance_seconds}s`} 
              variant="outlined" 
            />
          </Stack>
        </Box>
        
        {/* Data Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader aria-label="segment data table" size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Timestamp</TableCell>
                {dataColumns.map((column) => {
                  // Marketplace alias? Render the original key in the
                  // primary slot + listing title in smaller text, with
                  // a storefront icon. Mirror the FieldChip /
                  // FieldSelector treatment so the column reads the
                  // same way it does in the picker.
                  const meta = fieldMeta?.[column];
                  const mkt = meta?.marketplace;
                  if (mkt) {
                    const display = meta.display_name || meta.original_key || column;
                    const titleTip =
                      mkt.listing_title
                        ? (mkt.seller_label
                            ? `${mkt.listing_title} · by ${mkt.seller_label}`
                            : mkt.listing_title)
                        : column;
                    return (
                      <TableCell
                        key={column}
                        sx={{ fontWeight: 'bold', minWidth: 160 }}
                      >
                        <Tooltip title={titleTip}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'baseline',
                              gap: 0.5,
                            }}
                          >
                            <StorefrontIcon
                              sx={{
                                fontSize: 14,
                                color: 'primary.main',
                                alignSelf: 'center',
                              }}
                            />
                            <Box component="span">{display}</Box>
                            {mkt.listing_title && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.72rem', fontWeight: 500 }}
                              >
                                ({mkt.listing_title})
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell
                      key={column}
                      sx={{ fontWeight: 'bold', minWidth: 120 }}
                    >
                      {meta?.display_name ? (
                        <Tooltip title={`Key: ${column}`}>
                          <Box component="span">{meta.display_name}</Box>
                        </Tooltip>
                      ) : column}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {timestamps.map((timestamp, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatTimestamp(timestamp)}
                  </TableCell>
                  {dataColumns.map(column => (
                    <TableCell key={column}>
                      {dataObject[column] && dataObject[column][index] !== null
                        ? formatNumericValue(dataObject[column][index])
                        : 'N/A'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              
              {timestamps.length === 0 && (
                <TableRow>
                  <TableCell colSpan={dataColumns.length + 1} align="center">
                    <Typography variant="body2" sx={{ py: 2 }}>
                      No data available for this page
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination Controls */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            Showing page {current_page} of {total_pages}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TablePagination
              component="div"
              count={total_rows}
              page={current_page - 1}
              onPageChange={(e, page) => onPageChange(page + 1)}
              rowsPerPage={page_size}
              onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows:"
              labelDisplayedRows={() => `Page ${current_page} of ${total_pages}`}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SegmentDataPreviewModal; 